import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import { useRenameChatMutation } from 'tripPlanner/hooks/api/chat'
import { useDeleteTrip, useRecentTrips, useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import TripPlannerDrawer from '../../TripPlannerDrawer'
import { findClosestChat } from 'tripPlanner/assistant/utils/findClosestChat'
import { formatChatLabel } from 'tripPlanner/assistant/utils/format'
import { useAssistantHistory } from 'tripPlanner/assistant/hooks/useAssistantHistory'
import { useTripChatItineraryExport } from './useTripChatItineraryExport'

interface UseTripPlannerMenuDrawerArgs {
  variant?: ComponentProps<typeof TripPlannerDrawer>['variant']
  isLoggedIn: boolean
  isStaff: boolean
  isDevMode: boolean
  isRewindMode: boolean
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  onSearchChats?: () => void
  activeChatId?: number
  hasActiveChatItinerary?: boolean
  downloadItineraryAction?: ReactNode
  closeSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
}

export function useTripPlannerMenuDrawer({
  variant,
  isLoggedIn,
  isStaff,
  isDevMode,
  isRewindMode,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  onDevModeToggle,
  onRewindModeToggle,
  onSearchChats,
  activeChatId,
  hasActiveChatItinerary = false,
  downloadItineraryAction,
  closeSignal,
  onDrawerOpenChange = noop,
}: UseTripPlannerMenuDrawerArgs) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const closeDrawerRef = useRef<(() => void) | undefined>(undefined)
  const isDrawerOpenRef = useRef(false)
  const previousCloseSignalRef = useRef(closeSignal)
  const {
    chatHistory,
    recentChats,
    isHistoryListLoading,
    refetchChatList,
    deleteChatMutation,
  } = useAssistantHistory({ isLoggedIn })
  const { data: previousTrips, isLoading: isRecentTripsLoading, refetch: refetchTrips } = useRecentTrips({ enabled: isLoggedIn })
  const { mutateAsync: renameChatMutation } = useRenameChatMutation()
  const { downloadPdf, downloadSpreadsheet } = useTripChatItineraryExport({
    chatId: hasActiveChatItinerary ? activeChatId : undefined,
  })
  const { mutateAsync: deleteTripMutation } = useDeleteTrip({
    onSuccess: () => {
      refetchTrips()
    },
    onError: (error) => {
      console.error('[TripChat] Failed to delete trip', error)
      showSnackbar(intl.formatMessage({
        id: 'FtZ79RamZe2rLll_1NZF',
        defaultMessage: 'Unable to delete trip. Please try again.',
        description: 'Error snackbar message when trip deletion fails in the trip planner AI chat',
      }), 'critical')
    },
  })
  const { mutateAsync: updateTripMutation } = useUpdateTrip({
    onError: (error) => {
      console.error('[TripChat] Failed to rename trip', error)
      showSnackbar(intl.formatMessage({
        id: '5NH9uh8HGpEvAfHkilEf',
        defaultMessage: 'Unable to rename trip. Please try again.',
        description: 'Error snackbar message when trip rename fails in the trip planner AI chat',
      }), 'critical')
    },
  })

  const getChatDisplayName = useCallback((chat: TripPlannerChat | undefined) => {
    if (!chat) {
      return intl.formatMessage({
        id: '-LmD19gmaBgkOCUWrnKU',
        defaultMessage: 'this chat',
        description: 'Fallback display name for a trip planner chat when no chat is found',
      })
    }
    const trimmedName = chat.name?.trim()
    if (trimmedName) {
      return trimmedName
    }
    return formatChatLabel(getChatLastActivityTimestamp(chat), intl)
  }, [intl])

  const handleDeleteChat = useCallback(async(chatIdToDelete: number) => {
    const chatToDelete = chatHistory.find((chat) => chat.id === chatIdToDelete)
    const chatName = getChatDisplayName(chatToDelete)

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={chatName} />,
    )

    if (!confirmed) {
      return
    }

    const fallbackChatId = findClosestChat(chatHistory, chatIdToDelete)

    try {
      await deleteChatMutation({ chatId: chatIdToDelete })
      await refetchChatList()
      onChatDeleted(chatIdToDelete, fallbackChatId)
    } catch(error) {
      console.error('[TripChat] Failed to delete chat', error)
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [chatHistory, deleteChatMutation, getChatDisplayName, intl, onChatDeleted, refetchChatList, showModal])

  const handleDeleteTrip = useCallback(async(tripIdToDelete: string) => {
    const tripToDelete = previousTrips?.find((trip) => trip.id === tripIdToDelete)
    const tripName = tripToDelete?.name ?? intl.formatMessage({
      id: 'qNXsaB7GsvlaNr5QLIZf',
      defaultMessage: 'Untitled Trip',
      description: 'Fallback name for a trip without a title in the delete confirmation modal',
    })

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={tripName} type="trip" />,
    )

    if (!confirmed) {
      return
    }

    await deleteTripMutation(tripIdToDelete)
  }, [deleteTripMutation, intl, previousTrips, showModal])

  const handleRenameChat = useCallback(async(chatIdToRename: number, name: string) => {
    try {
      await renameChatMutation({ chatId: chatIdToRename, name })
      await refetchChatList()
    } catch(error) {
      console.error('[TripChat] Failed to rename chat', error)
      showSnackbar(intl.formatMessage({
        id: 'tvKzJmAgeic_SZz_ioDb',
        defaultMessage: 'Unable to rename chat. Please try again.',
        description: 'Error snackbar message when chat rename fails in the trip planner AI chat',
      }), 'critical')
      throw error
    }
  }, [intl, refetchChatList, renameChatMutation])

  const handleRenameTrip = useCallback(async(tripIdToRename: string, name: string) => {
    await updateTripMutation({ tripId: tripIdToRename, name })
    await refetchTrips()
  }, [refetchTrips, updateTripMutation])

  const isRecentItemsLoading = isLoggedIn && (isHistoryListLoading || isRecentTripsLoading)

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignalRef.current) return

    previousCloseSignalRef.current = closeSignal
    closeDrawerRef.current?.()
  }, [closeSignal])

  const handleCloseReady = useCallback((closeDrawer: () => void) => {
    closeDrawerRef.current = closeDrawer
  }, [])

  return useCallback(() => {
    if (isDrawerOpenRef.current) return

    isDrawerOpenRef.current = true
    onDrawerOpenChange(true)

    const drawerPromise = showModal<void>(
      <TripPlannerDrawer
        variant={variant}
        isLoggedIn={isLoggedIn}
        onStartNewChat={onStartNewChat}
        recentChats={recentChats}
        recentTrips={previousTrips}
        isRecentItemsLoading={isRecentItemsLoading}
        onChatSelect={onChatSelect}
        onChatRename={handleRenameChat}
        onTripRename={handleRenameTrip}
        onChatDelete={handleDeleteChat}
        onTripDelete={handleDeleteTrip}
        onSearchChats={onSearchChats}
        activeChatId={activeChatId}
        onActiveChatDownloadPdf={hasActiveChatItinerary ? downloadPdf : undefined}
        onActiveChatDownloadSpreadsheet={hasActiveChatItinerary ? downloadSpreadsheet : undefined}
        downloadItineraryAction={downloadItineraryAction}
        isStaff={isStaff}
        isDevMode={isDevMode}
        onDevModeToggle={onDevModeToggle}
        isRewindMode={isRewindMode}
        onRewindModeToggle={onRewindModeToggle}
        onCloseReady={handleCloseReady}
      />,
    )

    drawerPromise.finally(() => {
      isDrawerOpenRef.current = false
      closeDrawerRef.current = undefined
      onDrawerOpenChange(false)
    })
  }, [
    downloadItineraryAction,
    handleDeleteChat,
    handleDeleteTrip,
    handleRenameChat,
    handleRenameTrip,
    handleCloseReady,
    isDevMode,
    isLoggedIn,
    isRewindMode,
    isRecentItemsLoading,
    isStaff,
    onChatSelect,
    onDevModeToggle,
    onRewindModeToggle,
    onSearchChats,
    onDrawerOpenChange,
    onStartNewChat,
    activeChatId,
    downloadPdf,
    downloadSpreadsheet,
    hasActiveChatItinerary,
    previousTrips,
    recentChats,
    showModal,
    variant,
  ])
}
